import { pushEvent, pushToDataLayer } from '~/gtm/dataLayer';
import type { GA4UserData } from '~/gtm/types';

/**
 * Push login event after successful login
 */
export function pushLogin(method: string = 'email'): void {
  pushEvent('login', { method });
}

/**
 * Push sign_up event after successful registration
 */
export function pushSignUp(method: string = 'email'): void {
  pushEvent('sign_up', { method });
}

/**
 * Push user_data for Enhanced Conversions
 * This should be pushed before purchase events and on user identification
 */
export function pushUserData(
  userData: GA4UserData,
  visitorState: 'loggedin' | 'registered' | 'notregistered',
  visitorType: 'new' | 'returning' = 'returning'
): void {
  // Derive visitor_login_state from visitorState
  const visitorLoginState = visitorState === 'notregistered' ? 'visitor' : 'user';

  pushToDataLayer({
    event: 'user_data_ready',
    user_data: userData,
    visitor_type: visitorType,
    visitor_login_state: visitorLoginState,
    visitor_state: visitorState,
  });
}

/**
 * Helper to create user data from checkout/customer info
 */
export interface UserInfo {
  userId?: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  street?: string;
  city?: string;
  postcode?: string;
  countryCode?: string;
}

export function createUserData(userInfo: UserInfo): GA4UserData {
  const userData: GA4UserData = {};

  if (userInfo.userId) {
    userData.user_id = userInfo.userId;
  }

  if (userInfo.email) {
    // Normalize email as required by GA4 Enhanced Conversions
    userData.email_address = userInfo.email.toLowerCase().trim();
  }

  if (userInfo.phone) {
    userData.phone_number = userInfo.phone;
  }

  if (userInfo.firstName || userInfo.lastName || userInfo.street || userInfo.city) {
    userData.address = {};

    if (userInfo.firstName) {
      userData.address.first_name = userInfo.firstName;
    }
    if (userInfo.lastName) {
      userData.address.last_name = userInfo.lastName;
    }
    if (userInfo.street) {
      userData.address.street = userInfo.street;
    }
    if (userInfo.city) {
      userData.address.city = userInfo.city;
    }
    if (userInfo.postcode) {
      userData.address.postal_code = userInfo.postcode;
    }
    if (userInfo.countryCode) {
      userData.address.country = userInfo.countryCode;
    }
  }

  return userData;
}
